import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Box, Typography } from '@mui/material';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../config/firebase';
import axios from 'axios';

/**
 * SSO Token Handler — Option C (Firebase Custom Token).
 *
 * Reads `?customToken=...` from the URL (set by the portal's
 * launchAppWithSSO mint flow), exchanges it via signInWithCustomToken,
 * and lets AuthContext.onAuthStateChanged take over.
 *
 * Falls back to legacy `?token=...` (raw Firebase ID token POSTed to
 * /api/v1/google) for portals that haven't been upgraded yet.
 */
const SSOTokenHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  useEffect(() => {
    const handleSSOToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const customToken = urlParams.get('customToken');
      const firebaseToken = urlParams.get('token');

      if (!customToken && !firebaseToken) return;

      setIsAuthenticating(true);

      const cleanUrl = () => {
        window.history.replaceState({}, document.title, window.location.pathname);
      };

      // ---- Option C: Firebase Custom Token (preferred) ----
      if (customToken) {
        console.log('🔐 SSO custom token detected — signing in via Firebase...');
        try {
          if (!auth) throw new Error('Firebase auth not initialized');
          const result = await signInWithCustomToken(auth, customToken);
          console.log('✅ Firebase signInWithCustomToken OK:', result.user.email);
          cleanUrl();
          navigate('/', { replace: true });
        } catch (error: any) {
          console.error('❌ signInWithCustomToken failed:', error);
          cleanUrl();
          alert(`SSO Error: ${error?.message || 'Failed to sign in via SSO.'}`);
          navigate('/login', { replace: true });
        } finally {
          setIsAuthenticating(false);
        }
        return;
      }

      // ---- Legacy fallback: raw Firebase ID token ----
      try {
        const tokenParts = firebaseToken!.split('.');
        let email = '';
        let displayName = '';
        let uid = '';

        if (tokenParts.length === 3) {
          try {
            const payload = JSON.parse(atob(tokenParts[1]));
            email = payload.email || '';
            displayName = payload.name || payload.email || '';
            uid = payload.user_id || payload.sub || '';
          } catch (decodeError) {
            console.warn('Could not decode token payload:', decodeError);
          }
        }

        const response = await axios.post('/api/v1/google', {
          idToken: firebaseToken,
          email,
          displayName,
          uid,
        });

        const { access_token, token_type } = response.data || {};

        if (access_token) {
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('token_type', token_type || 'bearer');
          cleanUrl();
          navigate('/', { replace: true });
        } else {
          throw new Error('Backend returned no access_token');
        }
      } catch (error: any) {
        console.error('Legacy SSO failed:', error);
        cleanUrl();
        alert(`SSO Error: ${error.response?.data?.detail || error.message}`);
        navigate('/login', { replace: true });
      } finally {
        setIsAuthenticating(false);
      }
    };

    handleSSOToken();
  }, [navigate]);

  if (isAuthenticating) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography variant="h6">Authenticating via Single Sign-On...</Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

export default SSOTokenHandler;
